const COOKIE_NAME = "iris_amp_device_id";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year
const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

const DEFAULT_CAMPAIGN_CODE = "0";

/**
 * Extracts the campaign code from the utm_campaign string.
 * The code is expected as a single alphanumeric character appended after the last underscore.
 * e.g. "TikTok_Iris_US_iOS_AEO_Native_Subscribe_2026.06.15_A" → "A"
 *      "TikTok_Iris_US_iOS_AEO_Native_Subscribe_2026.06.15"   → DEFAULT_CAMPAIGN_CODE
 */
export function getCampaignCode(utm_campaign?: string): string {
  if (!utm_campaign) return DEFAULT_CAMPAIGN_CODE;
  const parts = utm_campaign.split("_");
  const lastSegment = parts[parts.length - 1] ?? "";
  return /^[A-Za-z0-9]$/.test(lastSegment)
    ? lastSegment
    : DEFAULT_CAMPAIGN_CODE;
}

function generateDeviceId(utm_campaign?: string): string {
  const campaignCode = getCampaignCode(utm_campaign);

  const randomBytes = new Uint8Array(5);
  crypto.getRandomValues(randomBytes);

  let suffix = "";
  for (let i = 0; i < randomBytes.length; i++) {
    suffix += CHARS[randomBytes[i] % CHARS.length];
  }

  return "~x" + campaignCode + suffix; // exactly 8 chars total — matches Webflow format
}

function getCookie(name: string): string | null {
  const encodedName = encodeURIComponent(name) + "=";
  const cookies = document.cookie ? document.cookie.split("; ") : [];

  for (const cookie of cookies) {
    if (cookie.indexOf(encodedName) === 0) {
      return decodeURIComponent(cookie.slice(encodedName.length));
    }
  }

  return null;
}

function setCookie(name: string, value: string): void {
  const parts = [
    `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
    "path=/",
    `max-age=${COOKIE_MAX_AGE}`,
    "SameSite=Lax",
  ];

  if (window.location.protocol === "https:") {
    parts.push("Secure");
  }

  const hostname = window.location.hostname;
  if (
    hostname === "chatwithiris.com" ||
    hostname.endsWith(".chatwithiris.com")
  ) {
    parts.push("domain=.chatwithiris.com");
  }

  document.cookie = parts.join("; ");
}

/**
 * Returns the persistent anonymous device ID, creating it if it doesn't exist.
 * Uses the `iris_amp_device_id` cookie — shared with the Webflow deployment.
 */
export function getOrCreateDeviceId(): string {
  const existing = getCookie(COOKIE_NAME);

  if (existing) {
    setCookie(COOKIE_NAME, existing); // refresh max-age on each visit
    return existing;
  }

  const utm_campaign =
    new URLSearchParams(window.location.search).get("utm_campaign") ??
    undefined;
  const newId = generateDeviceId(utm_campaign);
  setCookie(COOKIE_NAME, newId);
  return newId;
}
